import { MoreVerticalIcon } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "~/components/ui/menu";

type PendingAction =
  | {
      confirmLabel: string;
      description: string;
      destructive?: boolean;
      kind: "superAdmin";
      next: boolean;
      title: string;
    }
  | {
      confirmLabel: string;
      description: string;
      destructive?: boolean;
      kind: "status";
      next: "active" | "suspended";
      title: string;
    };

export function AdminUserActionsMenu(props: {
  busy: boolean;
  isSuperAdmin: boolean;
  onToggleStatus: (status: "active" | "suspended") => Promise<void>;
  onToggleSuperAdmin: (next: boolean) => Promise<void>;
  status: "active" | "suspended";
}) {
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [confirming, setConfirming] = useState(false);

  const confirmPending = async () => {
    if (!pending) {
      return;
    }
    setConfirming(true);
    try {
      if (pending.kind === "superAdmin") {
        await props.onToggleSuperAdmin(pending.next);
      } else {
        await props.onToggleStatus(pending.next);
      }
      setPending(null);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <div
        onClick={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
      >
        <Menu>
          <MenuTrigger
            render={
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="User actions"
                disabled={props.busy || confirming}
              />
            }
          >
            <MoreVerticalIcon className="size-4" />
          </MenuTrigger>
          <MenuPopup align="start" side="bottom" className="min-w-48">
            <MenuItem
              disabled={props.busy}
              onClick={() => {
                setPending(
                  props.isSuperAdmin
                    ? {
                        kind: "superAdmin",
                        next: false,
                        title: "Revoke super admin?",
                        description:
                          "This person will no longer bypass permission checks. They keep only the access granted by their roles.",
                        confirmLabel: "Revoke super admin",
                        destructive: true,
                      }
                    : {
                        kind: "superAdmin",
                        next: true,
                        title: "Make super admin?",
                        description:
                          "Super admins can manage every user and bypass normal permission checks. Use this only for trusted operators.",
                        confirmLabel: "Make super admin",
                      },
                );
              }}
            >
              {props.isSuperAdmin ? "Revoke super admin" : "Make super admin"}
            </MenuItem>
            <MenuItem
              variant={props.status === "active" ? "destructive" : "default"}
              disabled={props.busy}
              onClick={() => {
                setPending(
                  props.status === "active"
                    ? {
                        kind: "status",
                        next: "suspended",
                        title: "Suspend user?",
                        description:
                          "Suspended users cannot sign in or use Operators until they are activated again.",
                        confirmLabel: "Suspend user",
                        destructive: true,
                      }
                    : {
                        kind: "status",
                        next: "active",
                        title: "Activate user?",
                        description:
                          "This person will be able to sign in again with their Google account.",
                        confirmLabel: "Activate user",
                      },
                );
              }}
            >
              {props.status === "active" ? "Suspend" : "Activate"}
            </MenuItem>
          </MenuPopup>
        </Menu>
      </div>

      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !confirming) {
            setPending(null);
          }
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.title ?? "Confirm action?"}</AlertDialogTitle>
            <AlertDialogDescription>{pending?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" disabled={confirming} />}>
              Cancel
            </AlertDialogClose>
            <Button
              variant={pending?.destructive ? "destructive" : "default"}
              disabled={confirming}
              onClick={() => void confirmPending()}
            >
              {confirming ? "Working…" : (pending?.confirmLabel ?? "Confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}
